/**
 * Administration > Form Designer — per-tenant booking-form overrides
 * (rename/hide tabs, fields, slots; move, reorder, indent; custom tabs; dividers).
 * Logic lives in components/form-designer/useFormDesigner.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { listClients } from "@/lib/api";
import { TABS } from "@/components/booking-form/registry/tabs";
import { useFormDesigner } from "@/components/form-designer/useFormDesigner";
import { TabsCard } from "@/components/form-designer/TabsCard";
import { TabFieldsCard } from "@/components/form-designer/TabFieldsCard";
import { LayoutGrid, Save } from "lucide-react";

function useDesignerClient() {
  const { hasRole, assignments } = useAuth();
  const { viewAsClientId } = useViewAsClient();
  const isSuper = hasRole(["super_admin"]);
  const myClientIds = useMemo(
    () => assignments.filter((a) => a.client_id).map((a) => a.client_id),
    [assignments]
  );
  const [allClients, setAllClients] = useState([]);
  const [clientId, setClientId] = useState(viewAsClientId || myClientIds[0] || "");

  useEffect(() => {
    if (!isSuper) return;
    (async () => {
      const { data } = await listClients();
      setAllClients(data || []);
      if (!clientId && data?.length) setClientId(data[0].id);
    })();
  }, [isSuper, clientId]);

  useEffect(() => {
    if (viewAsClientId && viewAsClientId !== clientId) setClientId(viewAsClientId);
  }, [viewAsClientId, clientId]);

  return { isSuper, myClientIds, allClients, clientId, setClientId };
}

const Header = ({ c, fd }) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <div>
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <LayoutGrid className="h-6 w-6 text-slate-700" /> Form Designer
      </h1>
      <p className="text-sm text-slate-500">
        Rename tabs &amp; fields, move fields between tabs, reorder, mark required, hide.
        Changes apply to the New Booking form for this tenant only.
      </p>
    </div>
    <div className="flex items-center gap-2">
      {c.isSuper && (
        <Select value={c.clientId} onValueChange={c.setClientId}>
          <SelectTrigger className="w-[240px]" data-testid="fd-client-select">
            <SelectValue placeholder="Pick tenant" />
          </SelectTrigger>
          <SelectContent>
            {c.allClients.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Button onClick={fd.saveAll} disabled={fd.saving || !fd.dirtyCount} data-testid="fd-save-btn">
        <Save className="h-4 w-4 mr-1.5" />
        {fd.saving ? "Saving…" : fd.dirtyCount ? `Save ${fd.dirtyCount}` : "No changes"}
      </Button>
    </div>
  </div>
);

export default function AdminFormDesigner() {
  const c = useDesignerClient();
  const fd = useFormDesigner(c.clientId);

  if (!c.isSuper && !c.myClientIds.length) {
    return <Alert variant="destructive"><AlertDescription>You aren&apos;t assigned to any tenant.</AlertDescription></Alert>;
  }

  return (
    <div className="space-y-4" data-testid="admin-form-designer">
      <Header c={c} fd={fd} />
      {fd.loading ? (
        <Card><CardContent className="p-6 text-sm text-slate-500">Loading…</CardContent></Card>
      ) : (
        <>
          <TabsCard fd={fd} />
          {[...TABS.filter((t) => !t.editOnly), ...fd.customTabs].map((t) => (
            <TabFieldsCard key={t.key} fd={fd} tab={t} />
          ))}
          <p className="text-xs text-slate-400">
            Configure tabs, fields, indent groups, custom tabs, slot labels,
            help text and section dividers per tenant. Preview your changes by
            opening New Booking with <code>?bf=v2</code>.
          </p>
        </>
      )}
    </div>
  );
}
